import {
  EDifficultyType,
  EEntitlementType,
  ELightGalleryType,
  EOsceType,
  EPaceType,
  EProductType,
} from '@quesmed/types/models';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { AssetUploader } from '@/components';
import { EProductName } from '@/config';
import {
  AsyncSelect,
  cn,
  FormCheckBox,
  FormInput,
  FormSelect,
  FormTextArea,
  type ISelectItem,
  type ISelectItemSection,
} from '@/core/ui';
import { useGlobal, useRecord } from '@/store';
import { getSelectItem } from '@/utils';
import { ChoicesGrid } from '../question/choices/choices-grid';
import { AgentConfigForm } from './agent-config-form';
import { AiConfigForm } from './ai-config-form';
import { StationCoverField } from './cover-image';
// import { PhaseBriefsForm } from './phase-briefs';
import { PhaseTimersForm } from './phase-timers-form';
import { isPacesProduct } from './product';
import type { IStationSchema } from './schema';

const productEntitlements: ISelectItemSection[] = [
  {
    label: EProductName.OSCE,
    items: getSelectItem(EEntitlementType, [
      EEntitlementType.HISTORY_TAKING,
      EEntitlementType.COMMUNICATION_SKILLS,
      EEntitlementType.EXAMINATION,
      EEntitlementType.PROCEDURE,
      EEntitlementType.INTERPRETATION,
      EEntitlementType.ABCDE,
      EEntitlementType.PRESCRIBING,
      EEntitlementType.MIXED,
    ]),
  },
  {
    label: EProductName.PLAB2,
    items: getSelectItem(EEntitlementType, [
      EEntitlementType.INTERPRETATION,
      EEntitlementType.ABCDE,
      EEntitlementType.PRESCRIBING,
      EEntitlementType.MIXED,
    ]),
  },
  {
    label: EProductName.PACES,
    items: getSelectItem(EEntitlementType, [
      EEntitlementType.ABDOMINAL,
      EEntitlementType.CARDIOVASCULAR,
      EEntitlementType.COMMUNICATION,
      EEntitlementType.CONSULTATION,
      EEntitlementType.NEUROLOGY,
      EEntitlementType.RESPIRATORY,
    ]),
  },
];

const entitlementsByProductId: Partial<Record<EProductType, ISelectItem[]>> = {
  [EProductType.OSCE]: getSelectItem(EEntitlementType, [
    EEntitlementType.HISTORY_TAKING,
    EEntitlementType.COMMUNICATION_SKILLS,
    EEntitlementType.EXAMINATION,
    EEntitlementType.PROCEDURE,
    EEntitlementType.INTERPRETATION,
    EEntitlementType.ABCDE,
    EEntitlementType.PRESCRIBING,
    EEntitlementType.MIXED,
  ]),
  [EProductType.PLAB2]: getSelectItem(EEntitlementType, [
    EEntitlementType.INTERPRETATION,
    EEntitlementType.ABCDE,
    EEntitlementType.PRESCRIBING,
    EEntitlementType.MIXED,
  ]),
  [EProductType.PACES]: getSelectItem(EEntitlementType, [
    EEntitlementType.ABDOMINAL,
    EEntitlementType.CARDIOVASCULAR,
    EEntitlementType.COMMUNICATION,
    EEntitlementType.CONSULTATION,
    EEntitlementType.NEUROLOGY,
    EEntitlementType.RESPIRATORY,
  ]),
};

export const StationForm = ({ productId, className }: { productId?: EProductType; className?: string }) => {
  const form = useFormContext<IStationSchema>();
  const setChoicesGridCols = useRecord.use.setChoicesGridCols();
  const choicesGridCols = useRecord.use.choicesGridCols();
  const dbType = useGlobal.use.dbType();
  const isPaces = isPacesProduct(productId, dbType);
  useEffect(() => {
    setChoicesGridCols('2');
  }, []);

  return (
    <div className={cn('grid grid-cols-6 gap-x-4 gap-y-8 pb-4', className)}>
      <div className='grid grid-cols-3 grid-rows-3 gap-4 w-full col-span-6'>
        <div className='col-span-1 row-span-3'>
          <StationCoverField />
        </div>

        {/* <div className='bg-gray-200 rounded-xl'></div>
        <div className='bg-gray-300 rounded-xl'></div>

        <div className='bg-gray-200 rounded-xl'></div>
        <div className='bg-gray-300 rounded-xl'></div>

        <div className='bg-gray-200 rounded-xl'></div>
        <div className='bg-gray-300 rounded-xl'></div> */}

        <FormInput
          id='name'
          isRequired
          defaultValue={''}
          control={form.control}
          // labelPlacement='outside'
          name='name'
          placeholder='Enter name'
          className={'rounded'}
          variant='faded'
          label='Name'
        />
        <FormInput
          id='hiddenName'
          defaultValue={''}
          control={form.control}
          // labelPlacement='outside'
          name='hiddenName'
          placeholder='Enter hidden name'
          className={'rounded'}
          variant='faded'
          label='Hidden Name'
        />
        <FormSelect
          id='difficulty'
          isRequired
          variant='faded'
          label='Difficulty'
          className='rounded'
          control={form.control}
          transform={(value) => Number.parseInt(value, 10)}
          name='difficulty'
          placeholder='Select difficulty'
          options={Object.values(EDifficultyType)
            .filter((v) => typeof v === 'string' && v !== 'ALL')
            .map((type) => ({
              label: type as unknown as string,
              key: EDifficultyType[type as keyof typeof EDifficultyType].toString(),
            }))}
        />
        <FormSelect
          isRequired
          label='Entitlements'
          options={productId ? (entitlementsByProductId[productId] ?? []) : []}
          variant='faded'
          showSelected
          transformSelected={(a) => EEntitlementType[Number.parseInt(a, 10)]}
          name='entitlementIds'
          control={form.control}
          // transform={(a) => Number.parseInt(a, 10)}
          sections={productId ? undefined : productEntitlements}
          maxSelected={1}
          className='rounded'
          selectionMode='multiple'
        />
        <FormSelect
          id='osceTypeId'
          isRequired
          variant='faded'
          label={isPaces ? 'PACES Type' : 'OSCE Type'}
          transform={(value) => Number.parseInt(value, 10)}
          className='rounded'
          control={form.control}
          name='osceTypeId'
          placeholder={isPaces ? 'Select PACES type' : 'Select OSCE type'}
          options={
            isPaces
              ? Object.values(EPaceType)
                  .filter((v) => typeof v === 'string' && v !== 'ALL')
                  .map((type) => ({
                    label: type as unknown as string,
                    key: EPaceType[type as keyof typeof EPaceType].toString(),
                  }))
              : Object.values(EOsceType)
                  .filter((v) => typeof v === 'string' && v !== 'ALL')
                  .map((type) => ({
                    label: type as unknown as string,
                    key: EOsceType[type as keyof typeof EOsceType].toString(),
                  }))
          }
        />
        <AsyncSelect
          isRequired
          label='Topics'
          name='topicIds'
          control={form.control}
          showSelected
          queryKey='stationTopics'
          selectionMode='multiple'
          groupBy='options'
          className='rounded'
          variant='faded'
        />
      </div>

      <div className='col-span-6 -mt-8 flex justify-between'>
        <FormCheckBox control={form.control} name='aiEnabled'>
          AI Enabled
        </FormCheckBox>
        <ChoicesGrid count={2} />
      </div>
      <FormTextArea
        isRequired
        maxCount={100}
        showMarkdownEditor
        id='candidateBrief'
        control={form.control}
        defaultValue={''}
        name='candidateBrief'
        classNames={{
          input: cn('scrollbar-thin scrollbar-thumb-default-200 scrollbar-track-default-100', {
            'min-h-72': choicesGridCols === '1',
            'min-h-96': choicesGridCols === '2',
          }),
        }}
        placeholder='Enter candidate brief'
        className={cn('rounded', {
          'col-span-6': choicesGridCols === '1',
          'col-span-3': choicesGridCols === '2',
        })}
        variant='faded'
        label='Candidate Brief'
        endContent={<AssetUploader title='Candidate Brief' type={ELightGalleryType.CANDIDATE} />}
      />
      <FormTextArea
        isRequired
        maxCount={100}
        id='actorBrief'
        showMarkdownEditor
        control={form.control}
        defaultValue={''}
        name='actorBrief'
        classNames={{
          input: cn('scrollbar-thin scrollbar-thumb-default-200 scrollbar-track-default-100', {
            'min-h-72': choicesGridCols === '1',
            'min-h-96': choicesGridCols === '2',
          }),
        }}
        placeholder='Enter actor brief'
        className={cn('rounded', {
          'col-span-6': choicesGridCols === '1',
          'col-span-3': choicesGridCols === '2',
        })}
        variant='faded'
        label='Actor Brief'
        endContent={<AssetUploader title='Actor Brief' type={ELightGalleryType.ACTOR} />}
      />

      <FormTextArea
        isRequired
        maxCount={100}
        id='examinerBrief'
        showMarkdownEditor
        control={form.control}
        defaultValue={''}
        name='examinerBrief'
        classNames={{
          input: cn('scrollbar-thin scrollbar-thumb-default-200 scrollbar-track-default-100', {
            'min-h-72': choicesGridCols === '1',
            'min-h-96': choicesGridCols === '2',
          }),
        }}
        placeholder='Enter examiner brief'
        className={cn('rounded', {
          'col-span-6': choicesGridCols === '1',
          'col-span-3': choicesGridCols === '2',
        })}
        variant='faded'
        label='Examiner Brief'
        endContent={<AssetUploader title='Examiner Brief' type={ELightGalleryType.EXAMINER} />}
      />

      <FormTextArea
        isRequired
        maxCount={100}
        id='explanation'
        showMarkdownEditor
        control={form.control}
        defaultValue={''}
        name='explanation'
        classNames={{
          input: cn('scrollbar-thin scrollbar-thumb-default-200 scrollbar-track-default-100', {
            'min-h-72': choicesGridCols === '1',
            'min-h-96': choicesGridCols === '2',
          }),
        }}
        placeholder='Enter Walkthrough Brief'
        className={cn('rounded', {
          'col-span-6': choicesGridCols === '1',
          'col-span-3': choicesGridCols === '2',
        })}
        variant='faded'
        label='Walkthrough Brief'
        endContent={<AssetUploader title='Walkthrough Brief' type={ELightGalleryType.WALKTHROUGH} />}
      />

      <FormTextArea
        maxCount={100}
        id='llmActorBrief'
        showMarkdownEditor
        control={form.control}
        defaultValue={''}
        name='llmActorBrief'
        classNames={{
          input: cn('scrollbar-thin scrollbar-thumb-default-200 scrollbar-track-default-100', {
            'min-h-72': choicesGridCols === '1',
            'min-h-96': choicesGridCols === '2',
          }),
        }}
        placeholder='Enter LLM actor brief'
        className='col-span-6 rounded'
        variant='faded'
        label='LLM Actor Brief'
      />

      <div className='col-span-2 flex flex-col gap-4'>
        <PhaseTimersForm />
        <AgentConfigForm />
      </div>
      <AiConfigForm className='col-span-4' productId={productId} />
      {/* <PhaseBriefsForm /> */}
    </div>
  );
};
